/**
 * Supplier access policy for /settings/* routes.
 *
 * A supplier user can be the org-admin of their own sub-org, so the generic
 * `requiresAdmin` gate is not enough to keep them out of the parent org's
 * settings pages. This module owns the single source of truth for which
 * /settings paths a supplier may reach.
 *
 * Keep this allowlist in sync with the supplier filtering in
 * src/store/settings-navigation.ts (getCategories): suppliers only get
 * Organization > Users plus the category landing pages the nav surfaces.
 * The matching "Supplier Details" link lives under /supplier/* and is gated
 * separately by its own route meta.
 */

/** Paths (prefixes) under /settings a supplier user is allowed to open. */
export const SUPPLIER_SETTINGS_ALLOWLIST = [
  '/settings/users',
  '/settings/category/organization',
  '/settings/category/supplier',
];

/**
 * Whether a supplier user may navigate to the given path.
 * Non-/settings paths are not this policy's concern and return true.
 */
export function isSupplierAllowedSettingsPath(path: string): boolean {
  if (!path.startsWith('/settings')) return true;
  if (path === '/settings') return true;
  return SUPPLIER_SETTINGS_ALLOWLIST.some((allowed) => path.startsWith(allowed));
}

/**
 * Whether a route is off-limits to supplier users based on its meta.
 * Routes opt in by setting `blockedForSupplier: true` in their meta (e.g. open
 * search, knowledge graph) — those are hidden from the supplier nav and the
 * guard also blocks direct-URL access. Meta-based (not path-based) so it stays
 * decoupled from the route table.
 */
export function isRouteBlockedForSupplier(
  meta: { blockedForSupplier?: boolean } | undefined,
): boolean {
  return meta?.blockedForSupplier === true;
}
